import type { Data } from '@strapi/types';
import { useTranslations } from '@power-core/toolkit';
import { type FunctionComponent, useState } from 'react';

import { AbbClose } from '@/assets/icons/components/abb-close';
import { useAppContext } from '@/modules/app-context/use-app-context';
import { useFileUpload } from '@/modules/components/smart-cards/hooks/use-file-upload';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';
import { Button } from '@/ui-components/button/button';
import { SearchableDropdown } from '@/ui-components/dropdown/searchable-dropdown';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Modal } from '@/ui-components/modal/modal';
import { Paragraph } from '@/ui-components/paragraph/paragraph';
import { REGION_OPTIONS } from './constants';

export const UploadDocumentModal: FunctionComponent = () => {
	const t = useTranslations();
	const { isDarkTheme } = useTheme();
	const { documentTypes } = useAppContext();
	const { getSearchParams } = useNavigation();
	const { value } = getSearchParams();
	const { convertBytesToMB, file, fileInputRef, handleDrop, handleFileSelect, isPending, handleSubmit, onClear } = useFileUpload();
	const [region, setRegion] = useState<Data.Entity<'api::project-document.project-document'>['region']>('IEC');
	const documentType = documentTypes?.[Number.parseInt(value, 10)];

	return (
		<Modal
			name='UploadDocument'
			title={{ text: t('T_UPLOAD_TOOL_DOCUMENT', { documentType: t(documentType?.title ?? '') }), variant: 'h4' }}
			dismissible
			closeButton
		>
			<Flexbox direction='col' className='max-w-[600px] gap-3'>
				{documentType?.title === 'T_CURVES' && (
					<>
						<Paragraph variant='text-base'>{t('T_REGION')}</Paragraph>
						<SearchableDropdown
							className='w-full pr-0 '
							sizing={'medium'}
							options={REGION_OPTIONS}
							onSelect={(value) =>
								!Array.isArray(value) && setRegion(value as Data.Entity<'api::project-document.project-document'>['region'])
							}
							placeholder={t('T_SELECT_REGION')}
							isSearchable={false}
							externalSearchTerm={region as string}
							onClear={() => setRegion(undefined)}
							inputBackground='light'
						/>
					</>
				)}
				<label
					className={`
            relative p-12 border-2 border-dashed border-n400_input_stroker bg-white rounded-lg text-center  ${file ? 'cursor-not-allowed' : 'cursor-pointer hover:border-accent'}  dark:bg-bg_dark dark:text-n700_text_dark_mode
            `}
					onDrop={(e) => handleDrop(e, documentType?.title)}
					onDragOver={(e) => e.preventDefault()}
				>
					{file ? (
						<>
							<Paragraph variant='text-base' className='text-n400_input_stroker'>
								{file.name}
								<span className='text-xs text-n500_light_text'>{` ${convertBytesToMB(file.size)}MB`}</span>
							</Paragraph>
							<AbbClose
								width={18}
								height={18}
								fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'}
								className='cursor-pointer absolute top-2 right-2'
								onClick={onClear}
							/>
						</>
					) : (
						<Paragraph variant='text-base' className='text-n400_input_stroker'>
							{t('T_DRAG_AND_DROP')}
						</Paragraph>
					)}
					<input
						ref={fileInputRef}
						type='file'
						className='hidden'
						onChange={(e) => handleFileSelect(e, documentType?.title)}
						disabled={!!file}
					/>
				</label>
			</Flexbox>
			<Flexbox justify='end' className='mt-5 [&>*]:w-32'>
				<Button
					variant='primary'
					disabled={isPending || !file}
					isLoading={isPending}
					onClick={() => handleSubmit({ documentTypeName: documentType?.title ?? '', region })}
				>
					{t('T_UPLOAD')}
				</Button>
			</Flexbox>
		</Modal>
	);
};
